import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Plus, ArrowRight, Search, BookOpen, Sparkles, Share2 } from "lucide-react";

const STATUS = {
  not_started: { label: "Fresh", color: "#71717A", chip: "chip" },
  in_progress: { label: "In progress", color: "#7C5CFF", chip: "chip-blue" },
  reviewed: { label: "Reviewed", color: "#B8A4FF", chip: "chip-orange" },
  mastered: { label: "Mastered", color: "#5AC8FA", chip: "chip-green" },
  generating: { label: "Generating", color: "#B8A4FF", chip: "chip-purple" },
  failed: { label: "Failed", color: "#F87171", chip: "chip" },
};

export default function StudyList() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("date");

  const load = () => api.get("/subcourses").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const anyGenerating = (items || []).some((s) => s.generation_status && !["ready", "failed"].includes(s.generation_status));
  useEffect(() => {
    if (!anyGenerating) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [anyGenerating]);

  const retry = async (e, s) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await api.post(`/subcourses/${s.id}/retry`);
      toast.success("Retrying generation…");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't retry this study.");
    }
  };

  const SORTS = {
    date: (a, b) => (b.created_at || "").localeCompare(a.created_at || ""),
    name: (a, b) => (a.title || "").localeCompare(b.title || ""),
    mastery: (a, b) => (b.mastery_score || 0) - (a.mastery_score || 0),
  };
  const needle = q.trim().toLowerCase();
  const filtered = items
    ? items
        .filter((s) => !needle || s.title?.toLowerCase().includes(needle)
          || (s.subject_name || "").toLowerCase().includes(needle)
          || (s.tags || []).some((t) => t.toLowerCase().includes(needle)))
        .sort(SORTS[sort])
    : null;

  return (
    <div className="max-w-[1240px] mx-auto px-5 md:px-8 py-10 md:py-14">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-10">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="eyebrow">Library</div>
          <h1 className="display-2 mt-2">Your <span className="grad-text">studies</span></h1>
          <p className="text-[15px] text-[#B0AEBC] mt-2">
            {items === null ? "Loading…" : `${items.length} ${items.length === 1 ? "study" : "studies"} in your library.`}
          </p>
        </motion.div>
        <div className="flex items-center gap-2">
          <Link to="/app/my-packs">
            <button
              className="px-4 py-2 rounded-full bg-white/[0.04] border border-white/[0.06] hover:border-[#B8A4FF]/40 text-[13px] font-semibold text-[#B0AEBC] hover:text-white transition-colors flex items-center gap-1.5"
              data-testid="my-packs-link"
            >
              <Share2 size={13} /> My packs
            </button>
          </Link>
          <Link to="/app/studies/new">
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              className="btn btn-primary"
              data-testid="list-create-subcourse"
            >
              <Sparkles size={14} /> Compose a study
            </motion.button>
          </Link>
        </div>
      </header>

      {items && items.length > 0 && (
        <>
        <div className="flex flex-col sm:flex-row gap-3 mb-7">
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8E8B99] pointer-events-none" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by title, subject or tag"
              aria-label="Search your studies"
              className="input"
              style={{ paddingLeft: "2.75rem" }}
              data-testid="topic-search"
            />
          </div>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="input sm:w-[190px]"
            aria-label="Sort studies" data-testid="library-sort-select">
            <option value="date">Newest first</option>
            <option value="name">Name (A–Z)</option>
            <option value="mastery">Mastery (high–low)</option>
          </select>
        </div>
        {filtered.length === 0 && (
          <p className="text-[14px] text-[#8E8B99] mb-6" data-testid="library-no-results">No studies match "{q}".</p>
        )}
        </>
      )}

      {items === null ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => <div key={i} className="h-44 shimmer rounded-2xl" />)}
        </div>
      ) : items.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((s, i) => {
            const failed = s.generation_status === "failed";
            const isGen = !failed && s.generation_status && s.generation_status !== "ready";
            const meta = failed ? STATUS.failed : isGen ? STATUS.generating : (STATUS[s.status] || STATUS.not_started);
            return (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04, duration: 0.45 }}
                whileHover={{ y: -4 }}
              >
                <Link
                  to={`/app/studies/${s.id}`}
                  data-testid={`sc-card-${s.id}`}
                  className="surface surface-hover p-5 block h-full relative overflow-hidden group"
                >
                  {/* Color bar at top */}
                  <div
                    className="absolute top-0 left-0 right-0 h-1"
                    style={{ background: meta.color }}
                  />
                  <div className="flex items-center gap-2 mb-3 mt-1">
                    <span
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ background: meta.color }}
                    />
                    <span className="text-[11px] font-semibold" style={{ color: meta.color }} data-testid={`sc-card-status-${s.id}`}>
                      {meta.label}
                    </span>
                    {failed && (
                      <button onClick={(e) => retry(e, s)} className="ml-auto mr-6 text-[11px] font-semibold text-[#B8A4FF] hover:underline"
                        data-testid={`sc-card-retry-${s.id}`}>
                        Retry
                      </button>
                    )}
                  </div>
                  <h3 className="text-[17px] font-semibold leading-tight tracking-tight line-clamp-3 mb-3 min-h-[3.8rem]">
                    {s.title}
                  </h3>
                  <div className="flex flex-wrap gap-1 mb-4 min-h-[1.4rem]">
                    {(s.tags || []).slice(0, 3).map((t, j) => (
                      <span key={t} className={`chip ${["chip-blue", "chip-purple", "chip-orange"][j % 3]}`}>{t}</span>
                    ))}
                  </div>
                  <div className="pt-3 border-t border-white/8">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[11px] text-[#B0AEBC] font-medium">Mastery</span>
                      <span className="text-[13px] font-bold tabular-nums">{s.mastery_score || 0}%</span>
                    </div>
                    <div className="h-1.5 progress-track">
                      <motion.div
                        className="h-full progress-fill"
                        initial={{ width: 0 }}
                        whileInView={{ width: `${s.mastery_score || 0}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.9, delay: i * 0.04 }}
                      />
                    </div>
                  </div>
                  <ArrowRight size={14} className="absolute top-5 right-5 text-[#8E8B99] group-hover:text-[#7C5CFF] group-hover:translate-x-1 transition-all" />
                </Link>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const EmptyState = () => (
  <div className="surface p-16 text-center max-w-2xl mx-auto">
    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#7C5CFF]/15 via-[#B8A4FF]/15 to-[#7C5CFF]/15 flex items-center justify-center mx-auto mb-5">
      <BookOpen size={26} className="text-[#7C5CFF]" />
    </div>
    <h3 className="display-3">Nothing here yet</h3>
    <p className="text-[15px] text-[#B0AEBC] mt-3 mb-7 max-w-md mx-auto">
      Drop a PDF or paste any text. Your first study takes about a minute.
    </p>
    <Link to="/app/studies/new">
      <button className="btn btn-primary" data-testid="empty-list-create">
        <Sparkles size={14} /> Compose your first study
      </button>
    </Link>
  </div>
);
